'use client'

import { useMemo } from 'react'

import { useSession } from 'next-auth/react'

import { useInfiniteQuery } from '@tanstack/react-query'

import type { FeedItem } from '@/features/profiles/types/feed'
import { api } from '@/shared/services/api'

export type FeedPageResponse = {
  records: FeedItem[]
  pagination?: {
    page?: number
    page_count?: number
    total_count?: number
    per_page?: number
  }
}

export function useSocialFeedInfinite() {
  const { data: session, status } = useSession()
  const isAuthenticated = status === 'authenticated' && !!session?.accessToken

  const query = useInfiniteQuery({
    queryKey: ['social-feed', 'infinite'],
    enabled: isAuthenticated,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const qs = pageParam > 1 ? `?page=${pageParam}` : ''
      const { data } = await api.get<FeedPageResponse>(`/user/feed${qs}`)
      return data
    },
    getNextPageParam: lastPage => {
      const p = lastPage.pagination
      if (p?.page == null || p.page_count == null) return undefined
      return p.page < p.page_count ? p.page + 1 : undefined
    },
    staleTime: 3 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000
  })

  const records = useMemo(
    () => query.data?.pages.flatMap(p => p.records ?? []) ?? [],
    [query.data?.pages]
  )

  return { ...query, records }
}
